import React from 'react';
import { Reveal } from '../components/Reveal';
import { BookOpen, FlaskConical, ListChecks, XCircle, type LucideIcon } from 'lucide-react';

/**
 * "What it holds" -- marketing ruling M1 (2026-10-20), Phase A.
 *
 * Dropped from the ruling's draft, [verify] failed: "A rule that fails stays
 * on the library page with its reason." The library page does not render an
 * entry's `admission.status === 'held'` today, and Observer accounts only see
 * flagship entries (`filter_for_tier`), so the sentence is held for Phase B
 * ("the 'failed rule shown' line only when the library page renders
 * failures").
 */
interface Block {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  body?: string;
  bullets?: string[];
}

const blocks: Block[] = [
  {
    icon: BookOpen,
    eyebrow: 'Admitted',
    title: 'Trend-following on five asset-class ETFs',
    body:
      'US stocks, foreign developed stocks, US Treasuries, commodities and real estate: the rule published by Faber in 2007. Hold each ETF while its price is above its 10-month average; otherwise hold cash. Rebalanced monthly. Long-only, no borrowing.',
  },
  {
    icon: FlaskConical,
    eyebrow: 'In testing',
    title: 'Three more rules',
    body:
      'ETF momentum and crypto trend (both expected to be cleared for paper only), and global equity momentum.',
  },
  {
    icon: ListChecks,
    eyebrow: 'How a rule gets in',
    title: 'The test is written down first',
    bullets: [
      'We write the test down before we run it.',
      'We reproduce the published rule independently, and the platform’s result must match it.',
      'Trading costs are charged in the test.',
      'Any change to a rule is a new entry that starts over.',
    ],
  },
  {
    icon: XCircle,
    eyebrow: 'What is not in the library',
    title: 'Nothing discovered by an AI',
    body:
      'Nothing tuned to our data, no leverage, no shorting, no options, no futures, no currencies.',
  },
];

export const WhatItHolds: React.FC = () => {
  return (
    <section id="what-it-holds" className="py-28 px-6 scroll-mt-20">
      <div className="max-w-[1100px] mx-auto">
        <Reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm text-[#2dd4a0] font-medium uppercase tracking-widest mb-4">
            What it holds
          </p>
          <h2 className="font-heading text-3xl md:text-[2.75rem] font-bold text-white leading-tight">
            One rule admitted.{' '}
            <span className="text-gradient">Three in testing.</span>
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {blocks.map((b, i) => (
            <Reveal
              key={b.eyebrow}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-white/10 transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center text-white/60">
                  <b.icon size={20} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wider text-white/40">{b.eyebrow}</span>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">{b.title}</h3>
              {b.body && (
                <p className="text-white/60 leading-relaxed text-sm">{b.body}</p>
              )}
              {b.bullets && (
                <ul className="list-disc pl-5 space-y-1.5 text-white/60 leading-relaxed text-sm">
                  {b.bullets.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
